import { useEffect, useMemo } from 'react';
import moment, { Moment } from 'moment';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';

const DEFAULT_ISSUE_MIN_DAYS = 13;
const DEFAULT_ISSUE_DATE_FIELD = 'issueDate';
const DEFAULT_DATE_FROM_FIELD = 'dateFrom';

interface SickLeaveDateBoundsReturn {
	dateFromMax: Moment;
	dateFromMin: Moment;
	issueDateMax: Moment;
	issueDateMin: Moment;
}

export default function useSickLeaveDateBounds(): SickLeaveDateBoundsReturn {
	const { getValues, setValue } = useFormContext<FieldValues>();
	const issueDate = useWatch({ name: DEFAULT_ISSUE_DATE_FIELD });

	const issueMoment = useMemo(
		() =>
			moment(issueDate).isValid()
				? moment(issueDate).startOf('day')
				: moment().startOf('day'),
		[issueDate],
	);

	const bounds = useMemo(
		() => ({
			dateFromMin: issueMoment.clone().subtract(3, 'days'),
			dateFromMax: issueMoment.clone().add(1, 'day'),
		}),
		[issueMoment],
	);

	const issueDateMin = moment()
		.startOf('day')
		.subtract(DEFAULT_ISSUE_MIN_DAYS, 'days');
	const issueDateMax = moment().startOf('day');

	useEffect(() => {
		const dateFromRaw = getValues(DEFAULT_DATE_FROM_FIELD);
		if (!dateFromRaw || !moment(dateFromRaw).isValid()) {
			return;
		}

		const dateFromMoment = moment(dateFromRaw).startOf('day');
		if (
			dateFromMoment.isBefore(bounds.dateFromMin, 'day') ||
			dateFromMoment.isAfter(bounds.dateFromMax, 'day')
		) {
			setValue(DEFAULT_DATE_FROM_FIELD, null, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	}, [bounds, getValues, setValue]);

	return {
		issueDateMin,
		issueDateMax,
		...bounds,
	};
}
